import React, { useState, useEffect } from 'react';
import { Select, Skeleton } from 'antd';

/**
 * Custom selector for endpoint usage, based in ANT-D components Skeleton and Select.
 * endpointFetch should be an async function that returns a list in JSON API format,
 * with results readable through data['results'].
 * Triggers render based on the key value.
 */

const CustomSelector = ({
  endpointFetch,
  disabled = false,
  onChange = () => {},
  onChangeData = undefined,
  onLoad = undefined,
  loaderProps = {
    active: true,
    paragraph: false,
  },
  selectorProps = {
    allowClear: true,
    showSearch: true,
  },
  initialValue = undefined,
  selectorKey = 'cs',
  valueName = 'value',
  labelName = 'label',
  mode = undefined,
}) => {
  const [data, setData] = useState(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [value, setValue] = useState(initialValue);
  const [options, setOptions] = useState([]);

  useEffect(() => {
    async function fetchData() {
      setIsLoading(true);
      if (data) setValue(undefined);
      try {
        const { data: resData } = await endpointFetch();
        let options = [];
        if (onLoad) {
          let initialData = undefined;
          if (mode === 'multiple') {
            initialData = [];
            options = resData.results.map((item) => {
              if (initialValue && initialValue.includes(item[valueName]))
                initialData.push(item);
              return { value: item[valueName], label: item[labelName] };
            });
          } else {
            options = resData.results.map((item) => {
              if (item[valueName] === initialValue) initialData = item;
              return { value: item[valueName], label: item[labelName] };
            });
          }

          onLoad(initialData);
        } else {
          options = resData.results.map((item) => ({
            value: item[valueName],
            label:
              item[labelName] !== undefined && item[labelName] !== null
                ? item[labelName]
                : item[valueName],
          }));
        }
        setOptions(options);
        setData(resData.results);
        setIsLoading(false);
      } catch (err) {
        console.log(err);
      }
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectorKey]);

  useEffect(() => {
    if (value && initialValue === undefined) setValue(undefined);
  }, [initialValue]);

  const onSelfChange = (value) => {
    if (!onChangeData) onChange(value);
    else {
      if (mode === 'multiple') {
        onChangeData(
          value ? data.filter((item) => value.includes(item[valueName])) : [],
        );
      } else
        onChangeData(
          value ? data.find((item) => item[valueName] === value) : null,
        );
    }
    setValue(value);
  };

  return (
    <>
      <Skeleton
        active={true}
        paragraph={false}
        {...loaderProps}
        loading={isLoading}
      >
        <Select
          allowClear
          showSearch
          filterOption={(input, option) =>
            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
          }
          {...selectorProps}
          mode={mode}
          onChange={onSelfChange}
          loading={isLoading}
          value={value}
          disabled={disabled}
          key={selectorKey}
          options={options}
        />
      </Skeleton>
    </>
  );
};

export const CustomFormSelector = ({
  value,
  onChange,
  sendLabel = undefined,
  sendLabelDefault = '',
  valueName = 'value',
  labelName = 'label',
  onLoad = undefined,
  mode = undefined,
  ...customSelectorProps
}) => {
  const onChangeData = (cv) => {
    if (mode === 'multiple') {
      onChange(cv.map((i) => i[valueName]));
      sendLabel(cv.map((i) => i[labelName]).join(', ') || sendLabelDefault);
    } else {
      onChange(cv[valueName]);
      sendLabel(cv[labelName] || sendLabelDefault);
    }
  };

  const onSelfLoad = (cv) => {
    if (cv) {
      if (mode === 'multiple') {
        sendLabel(cv.map((i) => i[labelName]).join(', ') || sendLabelDefault);
      } else sendLabel(cv[labelName] || sendLabelDefault);
    }
    if (onLoad) onLoad(cv);
  };

  if (sendLabel)
    return (
      <CustomSelector
        initialValue={value}
        onChangeData={onChangeData}
        valueName={valueName}
        labelName={labelName}
        onLoad={onSelfLoad}
        mode={mode}
        {...customSelectorProps}
      />
    );
  return (
    <CustomSelector
      initialValue={value}
      onChange={onChange}
      valueName={valueName}
      labelName={labelName}
      onLoad={onLoad}
      mode={mode}
      {...customSelectorProps}
    />
  );
};

export default CustomSelector;
